import { SaleItemType } from '../../../components/Search/interfaces/product-action.dto';
import { Catalog, CatalogSectionCount } from '../../catalogs/catalog.types';
import { ESProductCatalogSearch } from '../../catalogs/products/product-catalog-es.types';
import { saleItemIsKit } from '../../catalogs/utils/saleItemType.utils';
import { ColorClassificationType } from '../../products/product.types.v2';
import {
  OfflineDatabaseCatalog,
  OfflineDatabaseCatalogSection,
} from '../database/offlineDatabase/types/offlineDatabaseCatalog.types';
import { OfflineProductColorPriceInfoTransformers } from './OfflineDatabaseProductColorPriceInfo.transformers';
import {
  SectionWithItemAndInfo,
  SectionWithKitAndInfo,
  SectionWithProductAndInfo,
} from './types/OfflineDatabaseCatalog.transformers.types';

export class OfflineCatalogTransformers {
  static toOnlineCatalog(data: OfflineDatabaseCatalog): Catalog {
    return {
      id: data.id,
      name: data.name,
      code: '',
      collections: [],
      active: data.active,
      endDate: data.endDate,
      position: data.position,
      imageUrl: data.imageUrl,
      startDate: data.startDate,
      isDefault: false,
      isDynamic: false,
      isLiveShop: false,
      liveLink: undefined,
      availability: undefined,
      classifications: undefined,
      ribbonBannerUrls: [],
      enablePublicSharing: false,
      groupColorsInSingleCard: false,
      ribbonBannerMobileUrls: [],
      showUnavailableProductsForSale: false,
      withMedia: false,
      navigationAvailabilities: [],
      navigationColorMode: undefined,
      navigationDeliveryWindows: undefined,
      navigationFilterRelatedProducts: false,
    };
  }

  static async toOnlineCatalogSections(
    sections: OfflineDatabaseCatalogSection[],
  ): Promise<CatalogSectionCount[]> {
    return sections.map((section) => ({
      catalogSectionId: section.id,
      catalogSectionName: section.name,
      catalogSectionPosition: section.position,
      itemsCount: section.items.length,
      isDefault: section.isDefault === true,
    }));
  }

  static async mapCatalogSectionItemToEntity(items: SectionWithItemAndInfo[]) {
    const mappedItems = await Promise.all(
      items.map((item) => {
        if (saleItemIsKit(item)) {
          return this.toCatalogKit(item);
        }
        return this.toESProductCatalogSearch(item);
      }),
    );

    return mappedItems;
  }

  static async toESProductCatalogSearch(
    item: SectionWithProductAndInfo,
  ): Promise<ESProductCatalogSearch> {
    const {
      section,
      product,
      productColor,
      sectionItem,
      allProductColors,
      productColorPriceInfo,
      productColorClassifications,
    } = item;

    const mappedProduct: ESProductCatalogSearch = {
      id: sectionItem.id,
      position: sectionItem.position,
      createdAt: new Date(),
      catalogSection: {
        active: true,
        id: section.id,
        name: section.name,
        createdAt: new Date(),
        position: section.position,
      },
      catalogSectionProduct: {
        id: sectionItem.id,
      },
      item: {
        tags: [],
        name: product.name,
        code: product.code,
        productId: product.id,
        referenceCode: product.referenceCode,
        productColorId: productColor.id,
        availability: productColor.availabilities,
        translations: [],
        prices:
          await OfflineProductColorPriceInfoTransformers.toCatalogSectionProductColorPrice(
            productColorPriceInfo,
          ),
        type: SaleItemType.PRODUCT_COLOR,
        medias: productColor.medias.map((media) => ({
          id: media.id,
          url: media.url,
          type: media.type,
          position: media.position,
        })),
        colorClassifications: productColorClassifications.map((classification) => ({
          id: classification.id,
          name: classification.name,
          code: classification.code,
          classificationType: {
            id: classification.classificationTypeId,
            useAsTag: true,
          } as ColorClassificationType,
        })),
        allColors: allProductColors.map((pc) => ({
          name: pc.name,
          code: pc.code,
          id: pc.colorId,
          hexcode: pc.hexcode,
          isImage: pc.isImage,
          imageUrl: pc.imageUrl,
          productColorId: pc.id,
          translations: [],
        })),
      },
    };

    return mappedProduct;
  }

  static async toCatalogKit(item: SectionWithKitAndInfo): Promise<ESProductCatalogSearch> {
    const { section, sectionItem, kit, type } = item;
    const mapped: ESProductCatalogSearch = {
      id: sectionItem.id,
      position: sectionItem.position,
      createdAt: new Date(),
      catalogSection: {
        active: true,
        id: section.id,
        name: section.name,
        createdAt: new Date(),
        position: section.position,
      },
      catalogSectionProduct: {
        id: sectionItem.id,
      },
      item: {
        type,
        kitId: kit.id,
        kitName: kit.name,
        kitCode: kit.code,
        kitWholesalePrice: kit.wholesalePrice,
        kitImageUrl: kit.imageUrl,
        kitAvailableForSale: true,
      },
    };

    return mapped;
  }
}
